import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useFetchWorkTimeTypes } from '../../hooks/useFetchWorkTimeTypes';
import { IWorkTime, IWorkTimeType } from '../../interface/workTimes';

/**
 * The props `SearchableSelect` needs to pick a work time's type, from the same list the picker
 * screen uses. Picking one fills the subject the way the picker screen does.
 */
export const useWorkTimeTypeSelect = () => {
  const { workTime, updateWorkTime } = useWorkTime();
  const { getAppLanguageCode } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: workTimeTypes,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchWorkTimeTypes({ limit: 50, search: searchValue });

  const appLanguage = getAppLanguageCode();
  const toOption = (type: IWorkTimeType): SelectOption => ({
    value: type.id,
    label: getTranslationFromTranslatableEntity(type.translations, appLanguage),
  });

  const options = workTimeTypes
    .map(toOption)
    .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label, appLanguage));
  const value = workTime?.workTimeType?.id ? toOption(workTime.workTimeType) : null;

  const onChange = (option: SelectOption) => {
    const workTimeType = workTimeTypes.find(({ id }: IWorkTimeType) => id === option.value);
    if (!workTimeType) return;
    const hasSubject = Boolean(workTime?.subject?.trim());
    const shouldSetSubject = !workTime?.isSubjectManuallyEdited || !hasSubject;
    updateWorkTime({
      ...workTime,
      workTimeType,
      ...(shouldSetSubject && {
        subject: toOption(workTimeType).label || workTime?.subject || '',
        isSubjectManuallyEdited: false,
      }),
    } as IWorkTime);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
